import React, { useState, useMemo } from 'react';
import { useAllJobs, useEmployers, useJobRoles, useAdminJobDetails } from '../../hooks/useAdmin';
import { getJobStatusLabel, getJobStatusColor } from '../../types/job';
import TableHeader from '../../components/common/TableHeader';
import JobDetailModal from '../../components/admin/JobDetailModal';
import './AdminJobs.css';

const AdminJobs = () => {
  const [filters, setFilters] = useState({
    status: 'all',
    employer_id: '',
    job_role_id: '',
    date: ''
  });
  
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  const [selectedJobId, setSelectedJobId] = useState(null);
  
  // Fetch employers and job roles for filter dropdowns
  const { data: employers } = useEmployers({});
  const { data: jobRoles } = useJobRoles();
  
  // Fetch selected job details
  const { data: selectedJobDetails, isLoading: isLoadingJobDetails } = useAdminJobDetails(selectedJobId);
  
  // Build backend filters (remove empty values)
  const backendFilters = useMemo(() => {
    const backendFilter = {};
    if (filters.status && filters.status !== 'all') {
      backendFilter.status = filters.status;
    }
    if (filters.employer_id) {
      backendFilter.employer_id = filters.employer_id;
    }
    if (filters.job_role_id) {
      backendFilter.job_role_id = filters.job_role_id;
    }
    // Use single date filter - set both start_date and end_date to the same date
    if (filters.date) {
      backendFilter.start_date = filters.date;
      backendFilter.end_date = filters.date;
    }
    return backendFilter;
  }, [filters]);
  
  // Fetch jobs with backend filters
  const { data: jobs, isLoading } = useAllJobs(backendFilters);

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  // Check if any filter is applied
  const hasActiveFilters = useMemo(() => {
    return (
      filters.status !== 'all' ||
      filters.employer_id !== '' ||
      filters.job_role_id !== '' ||
      filters.date !== ''
    );
  }, [filters]);

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      status: 'all',
      employer_id: '',
      job_role_id: '',
      date: ''
    });
  };

  const sortedJobs = useMemo(() => {
    if (!jobs || !sortConfig.key) return jobs;
    
    return [...jobs].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'job_role':
          aVal = (a.job_role?.job_role_name || a.job_role_name || a.job_role || '').toLowerCase();
          bVal = (b.job_role?.job_role_name || b.job_role_name || b.job_role || '').toLowerCase();
          break;
        case 'employer':
          aVal = (a.employer_email || a.employer?.email || a.employer_name || '').toLowerCase();
          bVal = (b.employer_email || b.employer?.email || b.employer_name || '').toLowerCase();
          break;
        case 'date':
          aVal = (a.start_date || a.job_date || a.date) ? new Date(a.start_date || a.job_date || a.date).getTime() : 0;
          bVal = (b.start_date || b.job_date || b.date) ? new Date(b.start_date || b.job_date || b.date).getTime() : 0;
          break;
        case 'hours':
          aVal = a.total_hours || 0;
          bVal = b.total_hours || 0;
          break;
        case 'status':
          aVal = a.status?.toLowerCase() || '';
          bVal = b.status?.toLowerCase() || '';
          break;
        case 'created':
          aVal = (a.created_at || a.date_created) ? new Date(a.created_at || a.date_created).getTime() : 0;
          bVal = (b.created_at || b.date_created) ? new Date(b.created_at || b.date_created).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [jobs, sortConfig]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  const handleViewDetails = (id) => {
    setSelectedJobId(id);
  };

  const handleCloseModal = () => {
    setSelectedJobId(null);
  };

  if (isLoading) {
    return (
      <div className="admin-jobs__loading">
        <div className="admin-jobs__spinner"></div>
        <p>Loading jobs...</p>
      </div>
    );
  }

  return (
    <div className="admin-jobs">
      <div className="admin-jobs__header">
        <h1 className="admin-jobs__title">All Jobs</h1>
        <div className="admin-jobs__filters">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="admin-jobs__filter"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="match_found">Match Found</option>
            <option value="accepted">Accepted</option>
            <option value="done">Done</option>
          </select>
          
          <select
            value={filters.employer_id}
            onChange={(e) => setFilters({ ...filters, employer_id: e.target.value })}
            className="admin-jobs__filter"
          >
            <option value="">All Employers</option>
            {employers && employers.map((employer) => (
              <option key={employer.id} value={employer.id}>
                {employer.practice_name || `${employer.first_name} ${employer.last_name}`}
              </option>
            ))}
          </select>
          
          <select
            value={filters.job_role_id}
            onChange={(e) => setFilters({ ...filters, job_role_id: e.target.value })}
            className="admin-jobs__filter"
          >
            <option value="">All Job Roles</option>
            {jobRoles && jobRoles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.job_role_name}
              </option>
            ))}
          </select>
          
          <input
            type="date"
            value={filters.date}
            onChange={(e) => setFilters({ ...filters, date: e.target.value })}
            className="admin-jobs__filter admin-jobs__filter--date"
            placeholder="Filter by Date"
          />
          
          <button
            onClick={handleResetFilters}
            className={`admin-jobs__reset-btn ${!hasActiveFilters ? 'admin-jobs__reset-btn--hidden' : ''}`}
            title="Reset all filters"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="admin-jobs__table-container admin-jobs__table-container--desktop">
        <table className="admin-jobs__table">
          <thead>
            <tr>
              <TableHeader 
                sortKey="job_role" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Job Role
              </TableHeader>
              <TableHeader 
                sortKey="employer" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Employer
              </TableHeader>
              <TableHeader 
                sortKey="date" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Date
              </TableHeader>
              <TableHeader 
                sortKey="hours" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Hours
              </TableHeader>
              <TableHeader 
                sortKey="status" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Status
              </TableHeader>
              <TableHeader 
                sortKey="created" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Created
              </TableHeader>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedJobs && sortedJobs.length > 0 ? (
              sortedJobs.map((job) => (
                <tr key={job.id}>
                  <td>{job.job_role?.job_role_name || job.job_role_detail?.job_role_name || job.job_role_name || job.job_role || 'N/A'}</td>
                  <td>{job.employer?.email || job.employer_email || job.employer_name || 'N/A'}</td>
                  <td>{formatDate(job.date || job.start_date || job.job_date)}</td>
                  <td>{job.number_of_hours || job.total_hours || job.hours || 'N/A'} {job.number_of_hours || job.total_hours || job.hours ? 'hours' : ''}</td>
                  <td>
                    <span
                      className="admin-jobs__status"
                      style={{ backgroundColor: getJobStatusColor(job.status) }}
                    >
                      {getJobStatusLabel(job.status)}
                    </span>
                  </td>
                  <td>{formatDate(job.created_at || job.date_created)}</td>
                  <td>
                    <button
                      onClick={() => handleViewDetails(job.id)}
                      className="admin-jobs__btn admin-jobs__btn--view"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="admin-jobs__empty">No jobs found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="admin-jobs__card-container admin-jobs__card-container--mobile">
        {sortedJobs && sortedJobs.length > 0 ? (
          sortedJobs.map((job) => (
            <div key={job.id} className="admin-jobs__card">
              <div className="admin-jobs__card-header">
                <div>
                  <h3 className="admin-jobs__card-role">{job.job_role?.job_role_name || job.job_role_detail?.job_role_name || job.job_role_name || job.job_role || 'N/A'}</h3>
                  <p className="admin-jobs__card-employer">{job.employer?.email || job.employer_email || job.employer_name || 'N/A'}</p>
                </div>
                <span
                  className="admin-jobs__status admin-jobs__status--mobile"
                  style={{ backgroundColor: getJobStatusColor(job.status) }}
                >
                  {getJobStatusLabel(job.status)}
                </span>
              </div>
              <div className="admin-jobs__card-body">
                <div className="admin-jobs__card-row">
                  <span className="admin-jobs__card-label">Date:</span>
                  <span className="admin-jobs__card-value">{formatDate(job.date || job.start_date || job.job_date)}</span>
                </div>
                <div className="admin-jobs__card-row">
                  <span className="admin-jobs__card-label">Hours:</span>
                  <span className="admin-jobs__card-value">{job.number_of_hours || job.total_hours || job.hours || 'N/A'} {job.number_of_hours || job.total_hours || job.hours ? 'hours' : ''}</span>
                </div>
                <div className="admin-jobs__card-row">
                  <span className="admin-jobs__card-label">Created:</span>
                  <span className="admin-jobs__card-value">{formatDate(job.created_at || job.date_created)}</span>
                </div>
              </div>
              <div className="admin-jobs__card-actions">
                <button
                  onClick={() => handleViewDetails(job.id)}
                  className="admin-jobs__btn admin-jobs__btn--view admin-jobs__btn--mobile"
                >
                  View Details
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="admin-jobs__empty admin-jobs__empty--mobile">No jobs found</div>
        )}
      </div>

      {selectedJobId && (
        <JobDetailModal
          job={selectedJobDetails}
          isLoading={isLoadingJobDetails}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default AdminJobs;

